import { useQuery } from '@tanstack/react-query'
import { Sparkles } from 'lucide-react'
import { useState } from 'react'

import { proposedKindsQuery, type ProposedKind } from '@/api/vocab'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Domain } from '@/schema'

import { KindEditorDialog } from './KindEditorDialog'

/**
 * "Create kind *balcony* from 3 proposals?"
 *
 * When Claude sees something the vocabulary has no word for, it returns a
 * `proposed_kind` string instead of forcing it into the nearest builtin
 * (§4.4.4 rule 4). This dialog is where those strings become real kinds —
 * or stay ignored, which is also a fine answer.
 *
 * Nothing here creates a kind by itself: picking one opens the normal
 * editor with the id pre-filled, so the roles are chosen deliberately from
 * the closed list rather than guessed.
 */

export interface ProposedKindsDialogProps {
  open: boolean
  onOpenChange(open: boolean): void
  /** Which domain a newly created kind lands in; the operator can change it. */
  defaultDomain?: Domain
}

export function ProposedKindsDialog({ open, onOpenChange, defaultDomain = 'region_kind' }: ProposedKindsDialogProps) {
  const query = useQuery({ ...proposedKindsQuery, enabled: open })
  const [domain, setDomain] = useState<Domain>(defaultDomain)
  const [creating, setCreating] = useState<ProposedKind | null>(null)

  const items = query.data?.items ?? []

  return (
    <>
      <Dialog open={open && !creating} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="size-5 text-info" aria-hidden />
              Words Claude wanted
            </DialogTitle>
            <DialogDescription>
              These came back from picture annotations as kinds the vocabulary does not have. Adding one makes it available everywhere; ignoring it changes nothing.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            <label className="mb-3 flex items-center gap-2 text-sm text-ink-2">
              Add new ones as
              <Select value={domain} onValueChange={(v) => setDomain(v as Domain)}>
                <SelectTrigger className="w-48" aria-label="Domain for new kinds">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Domain.options.map((d) => (
                    <SelectItem key={d} value={d}>
                      {d.replace(/_/g, ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>

            {query.isPending ? (
              <p className="text-sm text-ink-2">Looking…</p>
            ) : query.isError ? (
              <p className="text-sm text-ink-2">Could not read the suggestions right now.</p>
            ) : items.length === 0 ? (
              <p className="text-sm text-ink-2">Nothing suggested. Claude has been using words the vocabulary already knows.</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {items.map((item) => (
                  <li key={item.kind} className="flex items-center gap-3 rounded-xl border border-line p-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-ink">{item.kind.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-ink-3">
                        Seen in {item.count} suggestion{item.count === 1 ? '' : 's'}
                        {item.examples.length > 0 ? ` · e.g. ${item.examples[0]?.asset_uid}` : ''}
                      </p>
                    </div>
                    <Button size="sm" variant="secondary" onClick={() => setCreating(item)}>
                      Add it
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {creating ? (
        <KindEditorDialog
          open
          onOpenChange={(o) => {
            if (!o) setCreating(null)
          }}
          domain={domain}
          kind={null}
          suggestedId={creating.kind}
        />
      ) : null}
    </>
  )
}
